import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * A single side panel the conversation can hand to a tool call.
 *
 * A tool call renders *inside* the thread, so it cannot itself lay out a split
 * containing that thread. Instead it asks for the panel, and the page that owns
 * the layout decides how to show it. One slot, not a stack: two tools fighting
 * over the right-hand side would be a worse experience than the newer one simply
 * taking it.
 *
 * To add a kind, register a renderer at module scope next to the tool's
 * component (see dashboard-tool-call.tsx) and call `open` with its kind.
 */
export type SidePanelRequest = {
  /** Registry key picking the renderer — see registerSidePanel. */
  kind: string;
  /** What to show. Opaque to the panel machinery; the renderer interprets it. */
  id: string;
  /** Shown in the panel header. */
  title?: string;
};

export type SidePanelController = {
  content: SidePanelRequest | null;
  open: (request: SidePanelRequest) => void;
  close: () => void;
  /** True when this request is the one currently on screen. */
  isOpen: (kind: string, id: string) => boolean;
};

export const SidePanelContext = createContext<SidePanelController | undefined>(undefined);

export type SidePanelRenderer = React.ComponentType<{ id: string; title?: string }>;

const registry: Record<string, SidePanelRenderer> = {};

export function registerSidePanel(kind: string, renderer: SidePanelRenderer) {
  registry[kind] = renderer;
}

export function getSidePanelRenderer(kind: string): SidePanelRenderer | undefined {
  return registry[kind];
}

/**
 * Hook for tool calls. Returns undefined when no panel exists in this tree —
 * an embed, a historical thread rendered elsewhere — so callers can fall back
 * to their own presentation rather than silently doing nothing.
 */
export function useSidePanel(): SidePanelController | undefined {
  return useContext(SidePanelContext);
}

export const SidePanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<SidePanelRequest | null>(null);

  const open = useCallback((request: SidePanelRequest) => setContent(request), []);
  const close = useCallback(() => setContent(null), []);
  const isOpen = useCallback(
    (kind: string, id: string) => content?.kind === kind && content?.id === id,
    [content],
  );

  const value = useMemo<SidePanelController>(
    () => ({ content, open, close, isOpen }),
    [content, open, close, isOpen],
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};
